import {ChronologicalCommentsSort} from '@github-ui/commenting/ChronologicalCommentsSort'
import {useCommentEditsContext} from '@github-ui/commenting/CommentEditsContext'
import type {MarkdownComposerRef} from '@github-ui/commenting/useMarkdownBody'
import {VALUES as COMMENTING_VALUES} from '@github-ui/commenting/Values'
import {CopilotSurvey} from '@github-ui/copilot-survey-card'
import {dynamic} from '@github-ui/dynamic'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {IssueBody} from '@github-ui/issue-body/IssueBody'
import {useItemPickersContext} from '@github-ui/item-picker-base/ItemPickersContext'
import type {IssueViewerFeed} from '@github-ui/preheat-issues-manager/issue-viewer-storage-key'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {lazyWithPreload} from '@github-ui/react-core/lazy-with-preload'
import {useBeforeUnload} from '@github-ui/react-router'
import {useScribeArtifacts, useScribeArtifactsApiEnabled} from '@github-ui/scribe-artifacts'
import type {SubIssuesListItemNestedSubIssuesServerQueryQuery} from '@github-ui/server-persisted-queries/types/SubIssuesListItemNestedSubIssuesServerQuery'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {StickySidebar} from '@github-ui/sticky-sidebar'
import {nestedSubIssuesQueryKey} from '@github-ui/sub-issues/fetchNestedSubIssues'
import type {IssueViewerPreheaterProps, SubIssueSidePanelItem} from '@github-ui/sub-issues/sub-issue-types'
import {SubIssuesDataContext, useSubIssuesData} from '@github-ui/sub-issues/SubIssuesDataContext'
import {SubIssuesPresenceContext} from '@github-ui/sub-issues/SubIssuesPresenceContext'
import {useSubIssueState} from '@github-ui/sub-issues/SubIssueStateContext'
import {useCanEditSubIssues} from '@github-ui/sub-issues/useCanEditSubIssues'
import {useHasSubIssues} from '@github-ui/sub-issues/useHasSubIssues'
import {ISSUE_EVENTS} from '@github-ui/timeline-items/Events'
import {getHighlightedEventText} from '@github-ui/timeline-items/HighlightedEvent'
import {LABELS} from '@github-ui/timeline-items/Labels'
import {TimelineConnectorContext} from '@github-ui/timeline-items/TimelineDivider'
import {useStickyObserver} from '@github-ui/use-sticky-header/useStickyObserver'
import {Banner, Heading} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {clsx} from 'clsx'
import React, {Suspense, useCallback, useEffect, useId, useMemo, useRef, useState} from 'react'

import {CLASS_NAMES} from '../constants/class-names'
import {IDS} from '../constants/ids'
import {LABELS as VIEWER_LABELS} from '../constants/labels'
import {TEST_IDS} from '../constants/test-ids'
import {useInputElementActiveContext} from '../contexts/InputElementActiveContext'
import {useHash} from '../hooks/use-hash'
import {useIssueViewerDataPreheat} from '../hooks/use-issue-viewer-data-preheat'
import {canonicalIssueUrl} from '../utils/urls'
import {ArtifactsSection} from './artifacts-section/ArtifactsSection'
import {CreateArtifactMenuButton} from './artifacts-section/CreateArtifactMenuButton'
import {
  shouldFetchIssueArtifacts,
  shouldShowArtifactsSection,
  shouldShowCreateArtifactButton,
} from './artifacts-section/issue-artifacts-visibility'
import {IssueArtifactsAliveUpdates} from './artifacts-section/IssueArtifactsAliveUpdates'
import {LinkedArtifact} from './artifacts-section/LinkedArtifact'
import {ContentWrapper} from './ContentWrapper'
import {ConvertedToDiscussionBanner} from './ConvertedToDiscussionBanner'
import {EmuContributionBlockedBanner} from './EmuContributionBlockedBanner'
import {issueViewerSecondaryViewQueryKey} from './fetch-issue-viewer-secondary-view'
import {STICKY_HEADER_SIDEBAR_OFFSET} from './header/constants/sticky-elements'
import {Header} from './header/Header'
import {IssueHeaderDataContext, useIssueHeaderRefetch} from './header/IssueHeaderDataContext'
import {StickyHeader} from './header/StickyHeader'
import {useIssueMetadata} from './issue-metadata-query'
import type {IssueNode, IssueViewerPageViewerNode} from './issue-viewer-page-query-types'
import type {IssueSecondaryNode} from './issue-viewer-secondary-query-types'
import {IssueSidebar} from './IssueSidebar'
import {IssueTimelineErrorFallback} from './IssueTimelineErrorFallback'
import {IssueTimelineLoading} from './IssueTimelineLoading'
import styles from './IssueViewer.module.css'
import type {IssueDeletionConnection, OptionConfig} from './OptionConfig'
import {RecentAgentActivity} from './recent-agent-activity/RecentAgentActivity'
import {SidePanelMetadataDrawer} from './SidePanelMetadataDrawer'
import {SignedOutBanner} from './SignedOutBanner'
import {
  ActivityFilterEmptyState,
  ActivityFilterMenu,
  ActivityFiltersSetAsideNotice,
  ActivityLoadingState,
  ActivitySortMenu,
} from './timeline/ActivityTimelineControls'
import {CommentsHeader} from './timeline/CommentsHeader'
import type {TimelineFeed} from './timeline/fetch-timeline-items'
import type {IssueTimelineRenderProps} from './timeline/IssueTimeline'
import {ActivityEmptyState, TimelineActivityMenu} from './timeline/TimelineActivityMenu'
import {useActivityTimelineView} from './timeline/use-activity-timeline-view'
import {useIssueCommentsSort} from './timeline/use-issue-comments-sort'

const ISSUE_ARTIFACTS_PER_PAGE = 30

function IssueCommentComposerErrorFallback() {
  return (
    <Banner variant="critical" role="alert" title="Comment form unavailable">
      The comment form cannot be displayed. Reload the page to try again.
    </Banner>
  )
}

export type IssueViewerViewerFragment = Pick<
  IssueViewerPageViewerNode,
  | 'isEnterpriseManagedUser'
  | 'enterpriseManagedEnterpriseId'
  | 'login'
  | '__typename'
  | 'id'
  | 'name'
  | 'profileResourcePath'
  | 'avatarUrl'
>

type IssueViewerSecondaryDataProps = {
  secondaryIssueData?: IssueSecondaryNode
  initialNestedSubIssuesData?: SubIssuesListItemNestedSubIssuesServerQueryQuery
  initialNestedSubIssuesDataUpdatedAt?: number
  hideClosedSubIssuesEnabled?: boolean
  subIssueDeletionConnection?: IssueDeletionConnection
  timelinePage?: number
  issueUpdatesChannel?: string | null
  linkedArtifactNumber?: number
}

type IssueViewerCoreBaseProps = {
  optionConfig: OptionConfig
  viewer: IssueViewerViewerFragment | null
  feed: IssueViewerFeed
  /** Cursor the newest timeline window pins to, derived from the page payload by `timeline-prefill`. */
  initialBackAnchor?: string
  routeQueryKey?: readonly unknown[]
  isRepoOwnerEnterpriseManaged?: boolean | null
} & IssueViewerSecondaryDataProps

export type IssueViewerCoreIssue = Omit<IssueNode, 'backTimelineItems' | 'timelineItems' | 'totalCommentsCount'>

export type IssueViewerCoreProps = IssueViewerCoreBaseProps & {
  issue: IssueViewerCoreIssue
  renderTimeline: (props: IssueTimelineRenderProps) => React.ReactNode
  timelineAvailable: boolean
}

// `dynamic` rather than `React.lazy`: it resolves synchronously on the server, so the composer is
// in the SSR markup. Under `React.lazy` it was client-only behind a null fallback, which meant a
// chunk that failed to load left the page with no comment box at all.
const IssueCommentComposer = dynamic(
  async () => {
    const m = await import('@github-ui/commenting/IssueCommentComposer')
    return {default: m.IssueCommentComposer}
  },
  {displayName: 'IssueCommentComposer'},
)

const SubIssuesList = lazyWithPreload(async () => {
  const m = await import('@github-ui/sub-issues/SubIssuesList')
  return {default: m.SubIssuesList}
})

export function preheatIssueViewerSecondaryComponents() {
  return SubIssuesList.preload()
}

function SubIssueViewerPreheater({owner, repo, number}: IssueViewerPreheaterProps) {
  useIssueViewerDataPreheat({owner, repo, number, source: 'SubIssueTitle'})
  return null
}

const SubIssuesCreateDialog = React.lazy(async () => {
  const m = await import('@github-ui/sub-issues/SubIssuesCreateDialog')
  return {default: m.SubIssuesCreateDialog}
})

export function getPermalinkType(hash: string): 'issuecomment' | 'event' | 'cross_reference' | null {
  const hashWithoutSymbol = hash.startsWith('#') ? hash.substring(1) : hash
  if (!hashWithoutSymbol) return null
  if (hashWithoutSymbol.startsWith('issuecomment-')) return 'issuecomment'
  if (hashWithoutSymbol.startsWith('event-')) return 'event'
  if (hashWithoutSymbol.startsWith('ref-issue-') || hashWithoutSymbol.startsWith('ref-pullrequest-'))
    return 'cross_reference'
  return null
}

export function initialTimelineFeed(hash: string): TimelineFeed {
  const permalinkType = getPermalinkType(hash)
  return permalinkType === 'event' || permalinkType === 'cross_reference' ? 'activity' : 'comments'
}

type TimelineFeedContentProps = IssueTimelineRenderProps & {
  hideTimeline: boolean
  isPrimaryFeed?: boolean
  timelineAvailable: boolean
  renderTimeline: IssueViewerCoreProps['renderTimeline']
}

function TimelineFeedContent({
  hideTimeline,
  isPrimaryFeed = false,
  timelineAvailable,
  renderTimeline,
  viewer,
  ...timelineProps
}: TimelineFeedContentProps) {
  return (
    <div
      data-testid={isPrimaryFeed ? TEST_IDS.issueViewerCommentsContainer : undefined}
      className={isPrimaryFeed ? CLASS_NAMES.commentsContainer : undefined}
    >
      <div className={styles.commentsContainer}>
        {!hideTimeline && timelineAvailable && renderTimeline({viewer, ...timelineProps})}
        {!viewer && isPrimaryFeed && <SignedOutBanner />}
      </div>
    </div>
  )
}

export function resolveHasSubIssues(
  primaryHasSubIssues: boolean,
  secondaryIssueData:
    | {readonly subIssues?: {readonly nodes?: ReadonlyArray<unknown | null> | null} | null}
    | null
    | undefined,
  secondarySubIssuesPresenceEnabled: boolean,
): boolean {
  if (!secondarySubIssuesPresenceEnabled || secondaryIssueData == null) return primaryHasSubIssues
  return (secondaryIssueData.subIssues?.nodes ?? []).some(node => node != null)
}

export function IssueViewerCore({
  viewer,
  issue: primaryIssue,
  renderTimeline,
  timelineAvailable,
  feed,
  initialBackAnchor,
  optionConfig,
  isRepoOwnerEnterpriseManaged,
  secondaryIssueData,
  initialNestedSubIssuesData,
  initialNestedSubIssuesDataUpdatedAt,
  hideClosedSubIssuesEnabled,
  subIssueDeletionConnection,
  timelinePage,
  issueUpdatesChannel,
  linkedArtifactNumber,
  routeQueryKey,
}: IssueViewerCoreProps) {
  const primaryIdentifier = useMemo(
    () =>
      optionConfig.accountIssueOption?.isAccountIssue
        ? {permalink: optionConfig.accountIssueOption.permalink}
        : {
            owner: primaryIssue.repository.owner.login,
            repo: primaryIssue.repository.name,
            number: primaryIssue.number,
          },
    [optionConfig.accountIssueOption, primaryIssue.number, primaryIssue.repository],
  )
  const primaryReconciliation = useMemo(
    () => ({identifier: primaryIdentifier, queryKey: routeQueryKey}),
    [primaryIdentifier, routeQueryKey],
  )
  const {issue} = useIssueMetadata(
    primaryIssue,
    optionConfig.allowedProjectOwner,
    issueUpdatesChannel,
    primaryReconciliation,
  )
  const [currentHash, , hashHydrated] = useHash()
  const [initialHash, setInitialHash] = useState<string | null>(null)
  const sharedSubIssuesData = useSubIssuesData({
    issue: secondaryIssueData,
    parentIssueId: issue.id,
    owner: issue.repository.owner.login,
    repo: issue.repository.name,
    number: issue.number,
    initialData: initialNestedSubIssuesData,
    initialDataUpdatedAt: initialNestedSubIssuesDataUpdatedAt,
    updatesChannel: issueUpdatesChannel,
  })

  // Fire onLoad callback when issue data is available (component has mounted after Suspense resolved)
  const onLoad = optionConfig.onLoad
  useEffect(() => {
    onLoad?.()
  }, [onLoad])

  const primaryHasSubIssues = useHasSubIssues(issue)
  // Gate the section on the rows the TanStack list actually renders (non-null nodes), not the
  // Relay `subIssuesSummary.total`, which lags and can keep the section visible at zero. Fall
  // back to the summary until the non-Suspense secondary query resolves, so it doesn't vanish.
  const hasSubIssues = resolveHasSubIssues(primaryHasSubIssues, sharedSubIssuesData ?? secondaryIssueData, true)
  const canEditSubIssues = useCanEditSubIssues(secondaryIssueData)
  const {hideTimeline = false} = optionConfig
  // Follows the rendered feed, which also reflects org rollouts the client flag misses.
  const activityTabEnabled = feed === 'comments'
  const showActivityTab = activityTabEnabled && !hideTimeline
  const currentPermalinkType = getPermalinkType(currentHash)

  if (hashHydrated && initialHash === null) {
    setInitialHash(currentHash)
  }

  useEffect(() => {
    if (initialHash === null) return

    const permalinkType = getPermalinkType(initialHash)
    if (permalinkType) sendEvent('issue_viewer.permalink.viewed', {permalink_type: permalinkType})
  }, [initialHash])

  // A page view with Comments and Activity split. The viewer remounts for each issue, so this sends once per issue.
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- a page view has no handler to send it from.
    if (initialHash === null || !showActivityTab) return

    sendEvent('issue_viewer.comments.viewed', {permalink_type: getPermalinkType(initialHash)})
  }, [initialHash, showActivityTab])

  const showEmuContributionBlockedBanner =
    viewer && !!viewer.enterpriseManagedEnterpriseId && !isRepoOwnerEnterpriseManaged
  const showCommentComposer = viewer && !showEmuContributionBlockedBanner && !optionConfig.isReadonly
  // Crawler pages (?timeline_page=) always stay oldest first, and Activity stays unfiltered.
  const commentsSortable = showActivityTab && !timelinePage
  const {sort: commentsSort, setSort: setCommentsSort} = useIssueCommentsSort(commentsSortable)
  const commentComposerAtTop = commentsSort === ChronologicalCommentsSort.NewestFirst
  const activityHighlightedEvent =
    currentPermalinkType === 'issuecomment' ? undefined : getHighlightedEventText(currentHash, ISSUE_EVENTS)
  const activityView = useActivityTimelineView({
    enabled: commentsSortable,
    highlightedEvent: activityHighlightedEvent,
    commentsSort,
  })
  const activityFilterButtonRef = useRef<HTMLButtonElement>(null)
  const commentsHeadingId = useId()
  const issueArtifactsResourceId = secondaryIssueData?.databaseId ?? 0
  const scribeArtifactsApiEnabled = useScribeArtifactsApiEnabled()
  const [issueArtifactsPagination, setIssueArtifactsPagination] = useState({resourceId: 0, page: 1})
  const issueArtifactsPage =
    issueArtifactsPagination.resourceId === issueArtifactsResourceId ? issueArtifactsPagination.page : 1
  const {data: issueArtifactsPayload, isPlaceholderData: isIssueArtifactsPlaceholderData} = useScribeArtifacts({
    resourceType: 'issue',
    resourceId: issueArtifactsResourceId,
    page: issueArtifactsPage,
    perPage: ISSUE_ARTIFACTS_PER_PAGE,
    enabled: scribeArtifactsApiEnabled && shouldFetchIssueArtifacts(issueArtifactsResourceId),
  })
  const issueArtifacts = issueArtifactsPayload?.artifacts
  const [pendingArtifactPageFocus, setPendingArtifactPageFocus] = useState<{resourceId: number; page: number} | null>(
    null,
  )
  const handleIssueArtifactsPageChange = useCallback(
    (page: number) => {
      setPendingArtifactPageFocus({resourceId: issueArtifactsResourceId, page})
      setIssueArtifactsPagination({resourceId: issueArtifactsResourceId, page})
    },
    [issueArtifactsResourceId],
  )
  const showCreateArtifactButton =
    scribeArtifactsApiEnabled && shouldShowCreateArtifactButton(issueArtifacts, issueArtifactsPage)
  const showArtifactsSection =
    scribeArtifactsApiEnabled && shouldShowArtifactsSection(issueArtifacts, issueArtifactsPage)
  const [focusArtifactsSectionOnShow, setFocusArtifactsSectionOnShow] = useState(false)
  const [focusCreateArtifactButtonOnShow, setFocusCreateArtifactButtonOnShow] = useState(false)
  const focusArtifactsSectionAfterPageLoad =
    pendingArtifactPageFocus?.resourceId === issueArtifactsResourceId &&
    pendingArtifactPageFocus.page === issueArtifactsPage &&
    !isIssueArtifactsPlaceholderData
  const handleInitialArtifactCreated = useCallback(() => {
    setFocusArtifactsSectionOnShow(true)
  }, [])
  const handleArtifactsSectionFocusTargetReady = useCallback(() => {
    setFocusArtifactsSectionOnShow(false)
    setPendingArtifactPageFocus(null)
  }, [])
  const handleLastArtifactDeleted = useCallback(() => {
    if (issueArtifactsPage > 1) {
      handleIssueArtifactsPageChange(issueArtifactsPage - 1)
      return
    }
    setFocusCreateArtifactButtonOnShow(true)
  }, [handleIssueArtifactsPageChange, issueArtifactsPage])
  const handleCreateArtifactButtonFocusTargetReady = useCallback(() => {
    setFocusCreateArtifactButtonOnShow(false)
  }, [])

  const effectiveOptionConfig = useMemo(
    () => (showEmuContributionBlockedBanner ? {...optionConfig, showIssueCreateButton: false} : optionConfig),
    [optionConfig, showEmuContributionBlockedBanner],
  )
  const configuredOnSubIssueClick = optionConfig.onSubIssueClick
  const notifySubIssueClick = useCallback<NonNullable<OptionConfig['onSubIssueClick']>>(
    (item, options) => configuredOnSubIssueClick?.(item, options),
    [configuredOnSubIssueClick],
  )
  const handleSubIssueClick = useCallback(
    (item: SubIssueSidePanelItem, fromSuccessfulOptimisticUpdate?: boolean) => {
      const parent = item.parentIssue
      const issueDeletionConnection: IssueDeletionConnection | undefined = parent
        ? {
            ownerId: parent.id,
            connectionPath: ['subIssues'],
            targets: [
              {
                queryKey: issueViewerSecondaryViewQueryKey(parent.owner, parent.repo, parent.number),
                revalidate: true,
                refetchType: 'all',
              },
              {queryKey: nestedSubIssuesQueryKey(parent.owner, parent.repo, parent.number)},
            ],
          }
        : subIssueDeletionConnection
      notifySubIssueClick(item, {
        ...(fromSuccessfulOptimisticUpdate === undefined ? {} : {fromSuccessfulOptimisticUpdate}),
        ...(issueDeletionConnection ? {issueDeletionConnection} : {}),
      })
    },
    [notifySubIssueClick, subIssueDeletionConnection],
  )

  const composerRef = useRef<MarkdownComposerRef>(null)
  const issueBodyRef = useRef<HTMLDivElement>(null)
  const {startCommentEdit, cancelCommentEdit, isCommentEditActive} = useCommentEditsContext()
  const issueBodyKey = `issue-${issue.id}-body`
  const {setInputElementState, clearInputElementStates} = useInputElementActiveContext()
  const {anyItemPickerOpen} = useItemPickersContext()

  // Reset comment and input states on issue change
  useEffect(() => {
    clearInputElementStates()
  }, [issue.id, clearInputElementStates])

  useEffect(() => {
    setInputElementState(IDS.itemPicker, anyItemPickerOpen())
  }, [anyItemPickerOpen, setInputElementState])

  // Whenever the issue body editing state changes, synchronize that state via callbacks
  const onIssueEditStateChange = optionConfig.onIssueEditStateChange
  useEffect(() => {
    onIssueEditStateChange?.(isCommentEditActive())
  }, [isCommentEditActive, onIssueEditStateChange])

  const onCommentReply = useCallback((quotedComment: string) => {
    composerRef.current?.setText(quotedComment)
    requestAnimationFrame(() => composerRef.current?.focus())
  }, [])

  const onCommentChange = useCallback(
    (id: string) => {
      startCommentEdit(COMMENTING_VALUES.localStorageKeys.issueComment('', issue.id, id))
      optionConfig.onCommentEditStart?.(id)
    },
    [issue.id, optionConfig, startCommentEdit],
  )
  const onCommentEditCancel = useCallback(
    (id: string) => {
      cancelCommentEdit(COMMENTING_VALUES.localStorageKeys.issueComment('', issue.id, id))
      optionConfig.onCommentEditCancel?.(id)
    },
    [cancelCommentEdit, issue.id, optionConfig],
  )

  const beforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (isCommentEditActive()) {
        event.preventDefault()
        return (event.returnValue = '')
      }
    },
    [isCommentEditActive],
  )

  useBeforeUnload(beforeUnload)

  const metadataContent = useMemo(
    () => (
      <IssueSidebar
        issue={issue}
        sidebarSecondaryKey={secondaryIssueData}
        lazySecondaryData={secondaryIssueData}
        viewer={viewer}
        optionConfig={optionConfig}
        routeQueryKey={routeQueryKey}
      />
    ),
    [issue, optionConfig, routeQueryKey, secondaryIssueData, viewer],
  )

  const metadataPane = useMemo(
    () => (
      <div className={styles.issueViewerMetadataPane} data-testid={TEST_IDS.issueViewerMetadataPane}>
        {isFeatureEnabled('issues_sticky_header') ? (
          <Heading as="h2" className="sr-only">
            {LABELS.metadataHeader}
          </Heading>
        ) : (
          <>
            <Heading as="h2" className={styles.metadataHeader}>
              {LABELS.metadataHeader}
            </Heading>
            <Heading as="h2" className={`${styles.largeScreenMetadataHeader} sr-only`}>
              {LABELS.metadataHeader}
            </Heading>
          </>
        )}
        {metadataContent}
      </div>
    ),
    [metadataContent],
  )

  const handleBodyEditStateChange = useCallback(
    (isEditing: boolean) => {
      optionConfig?.onIssueEditStateChange?.(isEditing)
      if (isEditing) {
        startCommentEdit(issueBodyKey)
      } else {
        cancelCommentEdit(issueBodyKey)
      }
    },
    [cancelCommentEdit, issueBodyKey, optionConfig, startCommentEdit],
  )
  const {createDialogOpen, activeIssueId, closeCreateDialog} = useSubIssueState()

  const {isSticky: isHeaderSticky, sentinelRef} = useStickyObserver()
  const stickyHeaderEnabled = isFeatureEnabled('issues_sticky_header')
  const [sidePanelOpen, setSidePanelOpen] = useState(false)
  const sidePanelReturnFocusRef = useRef<HTMLButtonElement>(null)

  // Expose the sticky header's measured height (plus any notification-shelf offset) as a CSS
  // variable on the viewer grid so descendant sticky elements (e.g. the sub-issues list header)
  // can stick flush against the header instead of relying on a hardcoded height.
  const viewerGridRef = useRef<HTMLDivElement>(null)
  const [stickyHeaderNode, setStickyHeaderNode] = useState<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!stickyHeaderNode || typeof ResizeObserver === 'undefined') return
    let raf = 0
    let last = -1
    const observer = new ResizeObserver(([entry]) => {
      const height = entry?.borderBoxSize?.[0]?.blockSize ?? entry?.contentRect.height ?? 0
      if (height === last) return
      last = height
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        viewerGridRef.current?.style.setProperty(
          '--issue-sticky-header-offset',
          `calc(var(--base-sticky-header-height, 0px) + ${height}px)`,
        )
      })
    })
    observer.observe(stickyHeaderNode)
    return () => {
      cancelAnimationFrame(raf)
      observer.disconnect()
    }
  }, [stickyHeaderNode])

  const issuePath = `/${issue.repository.owner.login}/${issue.repository.name}/issues/${issue.number}`
  const defaultIssueUrl = ssrSafeLocation.origin ? `${ssrSafeLocation.origin}${issuePath}` : issuePath
  const issueUrl = canonicalIssueUrl(defaultIssueUrl, optionConfig.accountIssueOption)

  const sharedHeaderData = useIssueHeaderRefetch({
    issue,
    headerSecondary: secondaryIssueData,
    issueUpdatesChannel,
    primaryReconciliation,
  })

  const timelineProps = {
    renderTimeline,
    issueBodyRef,
    issueSecondary: secondaryIssueData,
    viewer: viewer?.login ?? null,
    owner: issue.repository.owner.login,
    repo: issue.repository.name,
    number: issue.number,
    primaryFeed: feed,
    primaryQueryKey: routeQueryKey,
    primaryIdentifier,
    onCommentReply,
    onCommentChange,
    onCommentEditCancel,
    optionConfig,
    page: timelinePage,
    issueUpdatesChannel,
  }
  const commentsFeed = feed
  const commentsTimeline = (
    <TimelineConnectorContext value={!activityTabEnabled}>
      <TimelineFeedContent
        {...timelineProps}
        feed={commentsFeed}
        initialBackAnchor={initialBackAnchor}
        commentsHeadingId={showActivityTab ? commentsHeadingId : undefined}
        highlightedEvent={
          commentsFeed === 'comments' && currentPermalinkType !== 'issuecomment'
            ? undefined
            : getHighlightedEventText(currentHash, ISSUE_EVENTS)
        }
        hideTimeline={hideTimeline}
        timelineAvailable={timelineAvailable}
        sort={commentsSort}
        loadingState={
          commentsSortable ? <IssueTimelineLoading label={VIEWER_LABELS.timeline.loadingComments} /> : undefined
        }
        isPrimaryFeed
      />
    </TimelineConnectorContext>
  )
  const activityMenu = showActivityTab ? (
    <TimelineActivityMenu
      initiallyOpen={initialHash === null ? null : initialTimelineFeed(initialHash) === 'activity'}
      legacyHeader={!stickyHeaderEnabled}
      timelineKey={activityView.itemTypes?.join(',')}
      headerActions={
        commentsSortable ? (
          <>
            <ActivityFilterMenu
              filters={activityView.filters}
              onFiltersChange={activityView.setFilters}
              anchorRef={activityFilterButtonRef}
            />
            <ActivitySortMenu sort={activityView.sort} onSortChange={activityView.setSort} />
          </>
        ) : undefined
      }
      notice={
        activityView.filtersSetAside ? (
          <ActivityFiltersSetAsideNotice
            onRestore={activityView.restoreFilters}
            returnFocusRef={activityFilterButtonRef}
          />
        ) : undefined
      }
      timeline={
        <TimelineFeedContent
          {...timelineProps}
          feed="activity"
          itemTypes={activityView.itemTypes}
          emptyState={
            activityView.itemTypes ? (
              <ActivityFilterEmptyState
                onClearFilters={() => activityView.setFilters([])}
                returnFocusRef={activityFilterButtonRef}
              />
            ) : (
              <ActivityEmptyState />
            )
          }
          loadingState={<ActivityLoadingState />}
          initialBackAnchor={undefined}
          highlightedEvent={activityView.timelineHighlightedEvent}
          hideTimeline={hideTimeline}
          timelineAvailable={timelineAvailable}
          sort={activityView.sort}
        />
      }
    />
  ) : null
  // Keyed so the composer keeps its state when the sort moves it above or below the comments.
  const commentComposer = showCommentComposer && (
    <ErrorBoundary
      key="comment-composer"
      boundaryName="issue-comment-composer"
      fallback={<IssueCommentComposerErrorFallback />}
      critical
    >
      <Suspense fallback={null}>
        <IssueCommentComposer
          ref={composerRef}
          issue={{
            ...issue,
            state: sharedHeaderData.state,
            stateReason: sharedHeaderData.stateReason,
          }}
          issueSecondary={
            secondaryIssueData && {
              ...secondaryIssueData,
              viewerCanClose: sharedHeaderData.viewerCanClose,
              viewerCanReopen: sharedHeaderData.viewerCanReopen,
              viewerCanSetStatus: sharedHeaderData.viewerCanSetStatus,
              status: sharedHeaderData.status,
            }
          }
          viewer={viewer}
          onChange={() => {
            startCommentEdit(COMMENTING_VALUES.localStorageKeys.issueNewComment('viewer', issue.id))
            optionConfig.onCommentEditStart?.(IDS.newComment)
          }}
          onSave={() => {
            cancelCommentEdit(COMMENTING_VALUES.localStorageKeys.issueNewComment('viewer', issue.id))
            optionConfig.onCommentEditCancel?.(IDS.newComment)
          }}
          onCancel={() => {
            cancelCommentEdit(COMMENTING_VALUES.localStorageKeys.issueNewComment('viewer', issue.id))
            optionConfig.onCommentEditCancel?.(IDS.newComment)
          }}
          onNewIssueComment={optionConfig.onNewIssueComment}
          onStateChange={optionConfig.onIssueUpdate}
          fileUploadsEnabled
          insideSidePanel={optionConfig.insideSidePanel ?? false}
          placement={commentComposerAtTop ? 'top' : 'bottom'}
          headingHidden={activityTabEnabled}
        />
      </Suspense>
    </ErrorBoundary>
  )

  const issueContentArea = (
    <div
      className={clsx(
        styles.contentArea,
        optionConfig.useViewportQueries ? styles.contentArea_viewport : styles.contentArea_container,
      )}
    >
      <RecentAgentActivity
        issue={issue}
        // This panel renders above the issue body, so waiting for the secondary query would push
        // the body down once the permission arrives. Write access is a close enough proxy to paint
        // immediately, and the real permission overrides it as soon as it resolves.
        viewerCanUpdateMetadata={secondaryIssueData?.viewerCanUpdateMetadata ?? issue.repository.isWritable}
        issueBodyRef={issueBodyRef}
        issueNumber={issue.number}
        viewerId={viewer?.id}
      />
      <div ref={issueBodyRef} data-testid={TEST_IDS.issueViewerIssueContainer} tabIndex={-1}>
        <SubIssuesPresenceContext value={hasSubIssues}>
          <IssueBody
            issue={issue}
            issueBodySecondary={secondaryIssueData}
            secondaryKey={secondaryIssueData}
            onLinkClick={optionConfig.onLinkClick}
            onIssueEditStateChange={handleBodyEditStateChange}
            onIssueUpdate={optionConfig.onIssueUpdate}
            onCommentReply={onCommentReply}
            isIssueEditActive={isCommentEditActive}
            highlightedEventText={currentHash}
            insideSidePanel={optionConfig.insideSidePanel}
            isReadonly={optionConfig.isReadonly || false}
            issueBodyTaskListAction={
              showCreateArtifactButton ? (
                <CreateArtifactMenuButton
                  resourceId={issueArtifactsResourceId}
                  focusOnMount={focusCreateArtifactButtonOnShow}
                  onCreated={handleInitialArtifactCreated}
                  onFocusTargetReady={handleCreateArtifactButtonFocusTargetReady}
                />
              ) : undefined
            }
            issueUpdatesChannel={issueUpdatesChannel}
            primaryReconciliation={primaryReconciliation}
          />
        </SubIssuesPresenceContext>
      </div>
      {hasSubIssues && (
        <div
          data-testid={TEST_IDS.subIssuesIssueContainer}
          className={clsx(
            styles.subIssuesContainer,
            !isFeatureEnabled('issue_inline_avatars') && styles.subIssuesContainer_container,
          )}
        >
          <Suspense fallback={null}>
            <SubIssuesList
              issue={secondaryIssueData}
              parentIssueId={issue.id}
              subIssuesSummary={issue.subIssuesSummary}
              hideClosedSubIssuesEnabled={hideClosedSubIssuesEnabled}
              onSubIssueClick={optionConfig.onSubIssueClick ? handleSubIssueClick : undefined}
              insideSidePanel={optionConfig.insideSidePanel}
              readonly={!canEditSubIssues || optionConfig.isReadonly}
              issueViewerPreheater={SubIssueViewerPreheater}
            />
          </Suspense>
        </div>
      )}
      {showArtifactsSection && (
        <ArtifactsSection
          resourceId={issueArtifactsResourceId}
          repositoryId={issue.repository.databaseId ?? undefined}
          organizationId={
            issue.repository.owner.__typename === 'Organization'
              ? (issue.repository.owner.databaseId ?? undefined)
              : undefined
          }
          artifacts={issueArtifacts ?? []}
          canReorder={
            !!secondaryIssueData?.viewerCanUpdateMetadata && issue.repository.isWritable && !optionConfig.isReadonly
          }
          currentPage={issueArtifactsPage}
          hasNextPage={!isIssueArtifactsPlaceholderData && (issueArtifactsPayload?.has_next_page ?? false)}
          onPageChange={handleIssueArtifactsPageChange}
          focusOnMount={focusArtifactsSectionOnShow || focusArtifactsSectionAfterPageLoad}
          onFocusTargetReady={handleArtifactsSectionFocusTargetReady}
          onLastArtifactDeleted={handleLastArtifactDeleted}
        />
      )}
      {showActivityTab && (
        <CommentsHeader
          headingId={commentsHeadingId}
          sort={commentsSortable ? commentsSort : undefined}
          onSortChange={setCommentsSort}
          trailingControl={activityMenu}
        />
      )}
      {commentComposerAtTop && commentComposer}
      <ErrorBoundary boundaryName="issue-comments-timeline" fallback={<IssueTimelineErrorFallback />} critical>
        <Suspense fallback={<IssueTimelineLoading delayedShow />}>{commentsTimeline}</Suspense>
      </ErrorBoundary>
      {!commentComposerAtTop && commentComposer}
      {showEmuContributionBlockedBanner && <EmuContributionBlockedBanner />}
    </div>
  )

  return (
    <SubIssuesDataContext value={sharedSubIssuesData}>
      <IssueHeaderDataContext value={sharedHeaderData}>
        {scribeArtifactsApiEnabled && (
          <IssueArtifactsAliveUpdates channel={issueUpdatesChannel} resourceId={issueArtifactsResourceId} />
        )}
        {linkedArtifactNumber !== undefined && (
          <LinkedArtifact
            key={linkedArtifactNumber}
            number={linkedArtifactNumber}
            resourceId={issueArtifactsResourceId}
            issueUrl={issue.url}
          />
        )}
        {optionConfig.copilotSurveyBanner && <CopilotSurvey {...optionConfig.copilotSurveyBanner} />}
        {secondaryIssueData?.discussion && (
          <ConvertedToDiscussionBanner discussionUrl={secondaryIssueData?.discussion?.url ?? ''} />
        )}
        {stickyHeaderEnabled ? (
          <>
            <StickyHeader
              issueKey={secondaryIssueData}
              issueType={issue.issueType}
              linkedSecurityAlertsData={secondaryIssueData}
              trackedBySecondaryData={secondaryIssueData}
              subIssueSecondaryData={secondaryIssueData}
              issueNumber={issue.number}
              ownerLogin={issue.repository.owner.login}
              repoName={issue.repository.name}
              issueUrl={issueUrl}
              optionConfig={effectiveOptionConfig}
              isHeaderSticky={isHeaderSticky}
              onInfoClick={() => setSidePanelOpen(true)}
              infoButtonRef={sidePanelReturnFocusRef}
              rootRef={setStickyHeaderNode}
            />
            <div
              ref={viewerGridRef}
              className={clsx(
                styles.viewerGrid,
                optionConfig.useViewportQueries ? styles.viewerGrid_viewport : styles.viewerGrid_container,
              )}
            >
              <div className={styles.viewerTitle}>
                <Header
                  issue={issue}
                  linkedSecurityAlertsData={secondaryIssueData}
                  trackedBySecondaryData={secondaryIssueData}
                  subIssueSecondaryData={secondaryIssueData}
                  optionConfig={effectiveOptionConfig}
                  hideJumpToBottom={commentComposerAtTop}
                />
                <div ref={sentinelRef} className={styles.stickyHeaderSentinel} aria-hidden="true" />
              </div>
              <div className={styles.viewerContent}>{issueContentArea}</div>
              <div
                className={clsx(
                  styles.viewerSidePanel,
                  optionConfig.useViewportQueries ? styles.viewerSidePanel_viewport : styles.viewerSidePanel_container,
                )}
              >
                {!sidePanelOpen && (
                  <StickySidebar
                    topOffset={STICKY_HEADER_SIDEBAR_OFFSET}
                    recalculateOffsetOnResize={stickyHeaderEnabled}
                  >
                    {metadataPane}
                  </StickySidebar>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            <Header
              issue={issue}
              linkedSecurityAlertsData={secondaryIssueData}
              trackedBySecondaryData={secondaryIssueData}
              subIssueSecondaryData={secondaryIssueData}
              optionConfig={effectiveOptionConfig}
              hideJumpToBottom={commentComposerAtTop}
            />
            <div ref={sentinelRef} className={styles.stickyHeaderSentinel} aria-hidden="true" />
            <ContentWrapper>
              <div
                className={clsx(
                  styles.contentAndSidebarWrapper,
                  optionConfig.useViewportQueries
                    ? styles.contentAndSidebarWrapper_viewport
                    : styles.contentAndSidebarWrapper_container,
                )}
              >
                {issueContentArea}
                <div
                  data-testid={TEST_IDS.issueViewerMetadataContainer}
                  className={clsx(
                    styles.metadataSidebar,
                    optionConfig.useViewportQueries
                      ? styles.metadataSidebar_viewport
                      : styles.metadataSidebar_container,
                  )}
                >
                  {metadataPane}
                </div>
              </div>
            </ContentWrapper>
          </>
        )}
        {stickyHeaderEnabled &&
          sidePanelOpen &&
          (optionConfig.insideSidePanel ? (
            <SidePanelMetadataDrawer onClose={() => setSidePanelOpen(false)} returnFocusRef={sidePanelReturnFocusRef}>
              {metadataPane}
            </SidePanelMetadataDrawer>
          ) : (
            <Dialog
              title="Details"
              position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
              width="360px"
              returnFocusRef={sidePanelReturnFocusRef}
              onClose={() => setSidePanelOpen(false)}
            >
              {metadataPane}
            </Dialog>
          ))}
        {secondaryIssueData && createDialogOpen && activeIssueId === issue.id && (
          <Suspense fallback={null}>
            <SubIssuesCreateDialog
              open={createDialogOpen}
              setOpen={closeCreateDialog}
              issue={secondaryIssueData}
              dialogTitle={optionConfig.subIssueCreateDialogTitle}
              onCreateSuccess={({createMore}): void => {
                if (!createMore) {
                  closeCreateDialog()
                }
              }}
            />
          </Suspense>
        )}
      </IssueHeaderDataContext>
    </SubIssuesDataContext>
  )
}
